import React, { useEffect, useRef } from "react";

/**
 * Google Places Autocomplete bound to a single text input.
 *
 * Behaves like a normal <input> (pass `value`, `onChange`, className, etc.),
 * and additionally fires `onResolved(parsed)` when the user picks a suggestion,
 * where `parsed` is { line1, suburb, state, postcode, formatted } extracted
 * from the place's address components.
 *
 * Requires REACT_APP_GOOGLE_MAPS_API_KEY in the frontend env. If the key is
 * missing or the script fails to load, the field degrades to a plain text input.
 */

let mapsPromise = null;
function loadGoogleMaps() {
  if (window.google?.maps?.places) return Promise.resolve(window.google);
  if (mapsPromise) return mapsPromise;
  const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  if (!key) return Promise.reject(new Error("Missing REACT_APP_GOOGLE_MAPS_API_KEY"));
  // Google invokes this when the key is rejected (billing off, API not enabled,
  // referrer not allowed, etc.). Make the cause obvious in the console.
  window.gm_authFailure = () => {
    console.error(
      "[Google Maps] Key rejected — check that Billing is enabled and the " +
      "'Maps JavaScript API' + 'Places API' are enabled for this key's project, " +
      "and that http://localhost:3000/* is an allowed HTTP referrer."
    );
  };
  mapsPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.google);
    script.onerror = () => reject(new Error("Failed to load Google Maps script"));
    document.head.appendChild(script);
  });
  return mapsPromise;
}

function comp(place, type, short = false) {
  const c = place.address_components?.find((x) => x.types.includes(type));
  return c ? (short ? c.short_name : c.long_name) : "";
}

export function parsePlace(place) {
  const streetNumber = comp(place, "street_number");
  const route        = comp(place, "route");
  const subpremise   = comp(place, "subpremise");
  const street       = [streetNumber, route].filter(Boolean).join(" ");
  const line1        = (subpremise ? `${subpremise}/${street}` : street) || place.name || "";
  return {
    line1,
    suburb:   comp(place, "locality") || comp(place, "sublocality") || comp(place, "postal_town"),
    state:    comp(place, "administrative_area_level_1", true),
    postcode: comp(place, "postal_code"),
    formatted: place.formatted_address || "",
  };
}

export default function AddressAutocomplete({
  value,
  onChange,
  onResolved,
  country = "au",
  ...props
}) {
  const inputRef = useRef(null);
  // Keep the latest callback without re-binding the Google listener each render.
  const resolvedRef = useRef(onResolved);
  resolvedRef.current = onResolved;

  useEffect(() => {
    let cancelled = false;
    let listener = null;
    loadGoogleMaps()
      .then((google) => {
        if (cancelled || !inputRef.current) return;
        const ac = new google.maps.places.Autocomplete(inputRef.current, {
          fields: ["address_components", "name", "formatted_address"],
          componentRestrictions: country ? { country } : undefined,
        });
        listener = ac.addListener("place_changed", () => {
          const place = ac.getPlace();
          if (!place || !place.address_components) return;
          resolvedRef.current && resolvedRef.current(parsePlace(place));
        });
      })
      .catch((e) => console.warn("[AddressAutocomplete]", e.message));
    return () => {
      cancelled = true;
      if (listener && window.google?.maps?.event) {
        window.google.maps.event.removeListener(listener);
      }
    };
  }, [country]);

  return (
    <input
      ref={inputRef}
      value={value}
      onChange={(e) => onChange && onChange(e.target.value)}
      // Stop Enter (selecting a suggestion) from submitting the surrounding form.
      onKeyDown={(e) => { if (e.key === "Enter") e.preventDefault(); }}
      autoComplete="off"
      {...props}
    />
  );
}
